import { useEffect, useState } from "react";
import type { ActivityLogItem } from "../../../shared/types";
import { AtlShell } from "../../components/atelier";
import { Ico } from "../../components/shell";
import { api, ApiError } from "../../api";
import { alertDialog, confirmDialog } from "../../components/Dialog";

/** Human label + accent tone for each logged action. */
const ACTIONS: Record<string, { label: string; tone: "good" | "warn" | "info" | "muted" }> = {
  login: { label: "Signed in", tone: "good" },
  login_failed: { label: "Failed login", tone: "warn" },
  logout: { label: "Signed out", tone: "muted" },
  account_created: { label: "Account created", tone: "good" },
  account_deleted: { label: "Account deleted", tone: "warn" },
  account_updated: { label: "Account edited", tone: "info" },
  password_changed: { label: "Password changed", tone: "info" },
  quiz_created: { label: "Quiz created", tone: "good" },
  quiz_created_ai: { label: "Quiz created (AI)", tone: "good" },
  ai_generated: { label: "AI generation", tone: "info" },
  ai_generate_failed: { label: "AI generation failed", tone: "warn" },
  quiz_deleted: { label: "Quiz binned", tone: "warn" },
  quiz_restored: { label: "Quiz restored", tone: "info" },
  quiz_purged: { label: "Quiz purged", tone: "warn" },
  bin_emptied: { label: "Bin emptied", tone: "warn" },
  session_started: { label: "Game hosted", tone: "info" },
  session_ended: { label: "Game ended", tone: "muted" },
  session_force_ended: { label: "Game force-ended", tone: "warn" },
  impersonate_start: { label: "Impersonation", tone: "warn" },
  impersonate_stop: { label: "Impersonation ended", tone: "muted" },
  logs_cleared: { label: "Logs cleared", tone: "warn" },
};

const TONE: Record<string, { bg: string; fg: string; border: string }> = {
  good: { bg: "#eef6ee", fg: "#3a7d44", border: "#cfe6cf" },
  warn: { bg: "#fbf2f3", fg: "var(--atl-accent)", border: "#e2c5ca" },
  info: { bg: "#eef3fa", fg: "#3b6aa0", border: "#cdddee" },
  muted: { bg: "var(--atl-surface)", fg: "var(--atl-soft)", border: "var(--atl-line)" },
};

function fmtTime(ms: number): string {
  const d = new Date(ms);
  const now = Date.now();
  const diff = now - ms;
  const rel =
    diff < 60_000
      ? "just now"
      : diff < 3_600_000
        ? `${Math.floor(diff / 60_000)}m ago`
        : diff < 86_400_000
          ? `${Math.floor(diff / 3_600_000)}h ago`
          : `${Math.floor(diff / 86_400_000)}d ago`;
  const abs = d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${rel} · ${abs}`;
}

export default function Logs() {
  const [items, setItems] = useState<ActivityLogItem[] | null>(null);
  const [filter, setFilter] = useState("");

  function load() {
    setItems(null);
    api
      .listActivity({ action: filter || undefined, limit: 300 })
      .then((r) => setItems(r.items))
      .catch(() => setItems([]));
  }
  useEffect(load, [filter]);

  async function clearAll() {
    const ok = await confirmDialog({
      title: "Clear all logs?",
      message: "Every activity entry will be permanently deleted. A single 'Logs cleared' entry is kept for accountability.",
      tone: "danger",
      confirmLabel: "Clear logs",
    });
    if (!ok) return;
    try {
      await api.clearActivity();
      setFilter("");
      load();
    } catch (e) {
      alertDialog(e instanceof ApiError ? e.message : "Could not clear logs");
    }
  }

  return (
    <AtlShell active="Logs" title="Logs" sub="ACTIVITY ACROSS ALL ACCOUNTS">
      <div style={{ padding: 30, display: "flex", flexDirection: "column", gap: 16, maxWidth: 940 }}>
        <div className="atl-card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap", rowGap: 10 }}>
            <div style={{ flex: "1 1 180px" }}>
              <h3 className="atl-serif" style={{ fontSize: 19 }}>Activity</h3>
              <p style={{ fontSize: 13, color: "var(--atl-soft)", fontWeight: 500, marginTop: 2 }}>
                Most recent first. Retained for 30 days.
              </p>
            </div>
            <select className="atl-select" style={{ width: "auto" }} value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">All actions</option>
              {Object.entries(ACTIONS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <button className="atl-iconbtn" title="Refresh" onClick={load}>
              <Ico name="clock" size={15} />
            </button>
            <button
              className="atl-btn atl-btn--ghost"
              style={{ padding: "9px 15px", fontSize: 13, color: "var(--atl-accent)" }}
              disabled={!items || items.length === 0}
              onClick={clearAll}
            >
              <Ico name="trash" size={15} /> Clear logs
            </button>
          </div>

          {items === null ? (
            <p style={{ fontSize: 13, color: "var(--atl-faint)", fontWeight: 500 }}>Loading…</p>
          ) : items.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--atl-faint)", fontWeight: 500 }}>No activity recorded yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {items.map((it) => {
                const meta = ACTIONS[it.action] ?? { label: it.action, tone: "muted" as const };
                const t = TONE[meta.tone];
                return (
                  <div key={it.id} className="atl-row" style={{ padding: "10px 14px", alignItems: "center", gap: 12 }}>
                    <span
                      className="atl-chip"
                      style={{ flex: "0 0 auto", borderColor: t.border, color: t.fg, background: t.bg, fontWeight: 600 }}
                    >
                      {meta.label}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, overflowWrap: "anywhere" }}>
                        {it.actorName ?? "Someone"}
                        {it.detail ? <span style={{ color: "var(--atl-soft)", fontWeight: 500 }}> — {it.detail}</span> : null}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--atl-faint)", fontWeight: 500 }}>
                        {fmtTime(it.at)}
                        {it.ip ? ` · ${it.ip}` : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AtlShell>
  );
}
